import { Box, Collapse } from '@mui/material'
import { useState } from 'react'
import { GetManagementBuildings, GetManagementDrillDown } from '../../../../services/peerToPeer/ManagementService'
import { type ManagementBuildingRow, type ManagementDrillDownRow, type ManagementDrillLevel } from '../../../../models/peerToPeer/Management'
import { handleApiResponse } from '../../../../utils/apiHelpers'
import { BuildingTable } from './BuildingTable'
import { CompactSummaryTiles } from './SummaryTiles'
import { DrillRowHeader } from './DrillRowHeader'
import { NoResults } from './NoResults'
import { NEXT_LEVEL } from './managementScope'
import { styles } from './DrillNode.styles'

interface Props {
  level: ManagementDrillLevel
  row: ManagementDrillDownRow
  yearId: number
}

/**
 * Within-context accordion drill-down: a row expands in place to reveal its child rows;
 * a District expands to its individual-form table. Children load on first expand.
 */
export const DrillNode = ({ level, row, yearId }: Props): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [children, setChildren] = useState<ManagementDrillDownRow[] | null>(null)
  const [buildings, setBuildings] = useState<ManagementBuildingRow[] | null>(null)
  const isDistrict = level === 'District'

  const loadBuildings = async (): Promise<void> => {
    setBuildings(await handleApiResponse(GetManagementBuildings(row.organizationId, yearId)) ?? [])
  }

  const toggle = async (): Promise<void> => {
    const next = !open
    setOpen(next)
    if (!next || children != null || buildings != null) return
    if (isDistrict) {
      await loadBuildings()
    } else {
      const result = await handleApiResponse(GetManagementDrillDown(level, yearId, row.organizationId))
      setChildren(result?.rows ?? [])
    }
  }

  const drillContent = (): JSX.Element | null => {
    if (isDistrict) {
      if (buildings == null) return null
      return buildings.length > 0
        ? <BuildingTable rows={buildings} reportingYearId={yearId} onChanged={() => { void loadBuildings() }} />
        : <NoResults />
    }
    if (children == null) return null
    if (children.length === 0) return <NoResults />
    const childLevel = NEXT_LEVEL[level] as ManagementDrillLevel
    return <>{children.map(child => <DrillNode key={`${child.organizationId}-${yearId}`} level={childLevel} row={child} yearId={yearId} />)}</>
  }

  return (
    <Box data-testid={`p2pDrillNode-${row.organizationId}`} sx={styles.node(open)}>
      <DrillRowHeader
        name={row.name} rate={row.submissionRate} open={open}
        onToggle={() => { void toggle() }} organizationId={row.organizationId}
      />
      <Collapse in={open} unmountOnExit>
        <CompactSummaryTiles metrics={row} />
        <Box sx={styles.body}>{drillContent()}</Box>
      </Collapse>
    </Box>
  )
}
